import { useCallback, useEffect, useState } from 'react';

/**
 * Measures a fixed proposal chrome bar so page content can sit below it.
 * Returns a callback ref and the current height in pixels.
 */
export default function useStickyChromeOffset(enabled = true) {
  const [node, setNode] = useState(null);
  const [height, setHeight] = useState(0);
  const setRef = useCallback((el) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!enabled || !node) {
      setHeight(0);
      if (typeof document !== 'undefined') {
        document.documentElement.style.removeProperty('--proposal-chrome-offset');
      }
      return undefined;
    }

    const update = () => {
      const next = Math.round(node.getBoundingClientRect().height);
      setHeight((prev) => (prev === next ? prev : next));
    };

    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (observer) observer.observe(node);
    window.addEventListener('resize', update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [enabled, node]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!enabled || !height) {
      document.documentElement.style.removeProperty('--proposal-chrome-offset');
      document.documentElement.style.removeProperty('scroll-padding-top');
      return undefined;
    }
    document.documentElement.style.setProperty('--proposal-chrome-offset', `${height}px`);
    document.documentElement.style.scrollPaddingTop = `${height}px`;
    return () => {
      document.documentElement.style.removeProperty('--proposal-chrome-offset');
      document.documentElement.style.removeProperty('scroll-padding-top');
    };
  }, [enabled, height]);

  return [setRef, height];
}
